<template>
  <div class="dropdown-multiselect" ref="dropdownRef">
    <label class="dropdown-multiselect_label">
      <span>{{ label }}</span>
      <div class="tooltip" v-if="tooltip">
        <TooltipComponent :text="tooltip" position="top"></TooltipComponent>
      </div>
    </label>
    <!-- Trigger button -->
    <button
        type="button"
        class="dropdown-trigger"
        @click="toggleDropdown"
        :class="[{'is-active': isOpen}, {placeholder: selectedLabels.length==0}, {'has-error': hasError}]">
      <span class="btn-text">{{ selectedLabels.length ? selectedLabels.join(', ') : placeholder }}</span>
      <span class="caret bp-icon-Chevron"></span>
    </button>

    <!-- Dropdown menu -->
    <div v-if="isOpen" class="dropdown-menu">
      <ul class="dropdown-list">
        <li
            v-for="(option, index) in options"
            :key="index"
            class="dropdown-item">
          <label>
            <input
                type="checkbox"
                :value="option.value"
                v-model="selectedValues"
                class="dropdown-checkbox"/>
            <span class="custom-checkbox"></span>
            <span>{{ option.label }}</span>
          </label>
        </li>
      </ul>
    </div>

    <!-- Error message -->
    <span v-if="hasError" class="error-message">{{ errorMessage }}</span>

  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount, defineEmits, defineProps } from 'vue';
import TooltipComponent from '@/components/common/TooltipComponent/TooltipComponent.vue';

const emit = defineEmits(['updateFilters', 'update:modelValue']);

const props = defineProps({
  options: {
    type: Array as () => { label: string; value: string }[],
    required: true,
  },
  modelValue: {
    type: Array as () => string[],
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Select options',
  },
  label: {
    type: String,
    default: 'Label',
  },
  id: {
    type: String,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  tooltip: {
    type: String,
    required: false
  }
});

const isOpen = ref(false);
const selectedValues = ref<string[]>([...props.modelValue]);
const dropdownRef = ref<HTMLElement | null>(null);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const handleClickOutside = (event: MouseEvent) => {
  if (isOpen.value && dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

watch(selectedValues, (newValues) => {
  emit('updateFilters', props.id, newValues);
  if (JSON.stringify(newValues) !== JSON.stringify(props.modelValue)) {
    emit('update:modelValue', newValues);
  }
});

watch(
    () => props.modelValue,
    (newModelValue) => {
      if (JSON.stringify(newModelValue) !== JSON.stringify(selectedValues.value)) {
        selectedValues.value = [...newModelValue];
      }
    },
    { immediate: true }
);

const selectedLabels = computed(() =>
    props.options
        .filter((option) => selectedValues.value.includes(option.value))
        .map((option) => option.label)
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
@import 'DropdownMultiSelectComponent';
</style>